import React, { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

/**
 * If the browser has cookies blocked, we've already got the header-based
 * auth fallback working under the hood — but we owe the user a one-time
 * heads-up so they know their session will reset on browser restart
 * (localStorage still persists across restarts on most browsers, but not
 * in private/incognito modes). Detection is best-effort: try to write a
 * probe cookie, read it back, and clean up. If we can't read it, cookies
 * are effectively blocked.
 */
const DISMISS_KEY = "atlas.cookies-banner-dismissed";

function cookiesBlocked() {
    try {
        // Fast path — some browsers surface a boolean.
        if (navigator.cookieEnabled === false) return true;
    } catch {}
    try {
        const probe = "atlas_probe";
        document.cookie = `${probe}=1; path=/; SameSite=Lax`;
        const ok = document.cookie.split(";").some((c) => c.trim().startsWith(`${probe}=`));
        // Cleanup
        document.cookie = `${probe}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        return !ok;
    } catch {
        return true;
    }
}

export default function CookieCheckBanner() {
    const [show, setShow] = useState(false);

    useEffect(() => {
        try {
            if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
        } catch {}
        if (cookiesBlocked()) setShow(true);
    }, []);

    if (!show) return null;

    const dismiss = () => {
        try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch {}
        setShow(false);
    };

    return (
        <div
            className="fixed top-3 inset-x-3 z-[60] mx-auto max-w-2xl rounded-2xl bg-amber-50 border border-amber-200 shadow-lg px-4 py-3 flex items-start gap-3"
            data-testid="cookies-blocked-banner"
        >
            <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-4 h-4 text-amber-800" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-sm text-amber-900">
                    Cookies are blocked in this browser
                </div>
                <div className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                    Atlas will use header-based sessions instead — everything works, but you
                    may be signed out when you fully close the browser. Enable cookies for
                    this site to stay signed in across restarts.
                </div>
            </div>
            <button
                onClick={dismiss}
                className="text-amber-700 hover:text-amber-900 flex-shrink-0"
                data-testid="cookies-blocked-dismiss"
                aria-label="Dismiss"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}
